import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, CircularProgress, Menu, MenuItem, Stack, Typography, useTheme } from '@mui/material';
import TimeToLeaveIcon from '@mui/icons-material/TimeToLeave';
import EditCalendarIcon from '@mui/icons-material/EditCalendar';
import AllInboxIcon from '@mui/icons-material/AllInbox';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import WorkIcon from '@mui/icons-material/Work';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import LayersIcon from '@mui/icons-material/Layers';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import { useNavigate } from 'react-router-dom';
import { AxiosInstanceService } from '../../../services/axiosInstance';
import { apiRoute } from '../../../util/constant';
import DashboardMetricCard from './DashboardMetricCard';

const modules = [
  { mode: 'vehicle', title: 'Vehicles', path: '/vehicles', icon: <TimeToLeaveIcon /> },
  { mode: 'booking', title: 'Bookings', path: '/booking', icon: <EditCalendarIcon /> },
  { mode: 'shipment', title: 'Shipments', path: '/shipments', icon: <AllInboxIcon /> },
  { mode: 'container', title: 'Containers', path: '/containers', icon: <Inventory2Icon /> },
  { mode: 'job', title: 'Jobs', path: '/jobs', icon: <WorkIcon /> },
];

const getCountTotal = (items = []) => {
  const totalItem = items.find((item) => item?.name?.toString().toUpperCase() === 'TOTAL');
  if (totalItem) return Number(totalItem.count) || 0;

  return items.reduce((sum, item) => sum + (Number(item?.count) || 0), 0);
};

const getCountBreakdown = (items = []) =>
  items.filter((item) => item?.name?.toString().toUpperCase() !== 'TOTAL');

const AdminDashboardHome = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [shipmentMenuAnchor, setShipmentMenuAnchor] = useState(null);

  const openShipmentMenu = (event) => setShipmentMenuAnchor(event.currentTarget);
  const closeShipmentMenu = () => setShipmentMenuAnchor(null);
  const handleAddShipment = (shipmentMode) => {
    closeShipmentMenu();
    navigate(`/addShipment?shipmentMode=${encodeURIComponent(shipmentMode)}`);
  };

  useEffect(() => {
    let mounted = true;

    const loadCounts = async () => {
      try {
        setLoading(true);
        const instance = await AxiosInstanceService.getAxiosInstance();
        const results = await Promise.allSettled(
          modules.map(async (module) => {
            const response = await instance.get(`${apiRoute.getManage}${module.mode}/count`);
            return [module.mode, Array.isArray(response.data) ? response.data : []];
          })
        );

        if (mounted) {
          const fulfilled = results
            .filter((result) => result.status === 'fulfilled')
            .map((result) => result.value);

          setCounts(Object.fromEntries(fulfilled));
          setError(results.some((result) => result.status === 'rejected') ? 'Some dashboard sections could not be loaded.' : '');
        }
      } catch (err) {
        if (mounted) setError(err?.response?.data?.message || err.message || 'Unable to load dashboard');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadCounts();
    return () => {
      mounted = false;
    };
  }, []);

  const totalOperations = useMemo(
    () => modules.reduce((sum, module) => sum + getCountTotal(counts[module.mode]), 0),
    [counts]
  );

  if (loading) {
    return (
      <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Typography sx={{ color: '#b42318', mb: 2, fontWeight: 700 }}>
          {error}
        </Typography>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(5, minmax(0, 1fr))' },
          gap: 2,
        }}
      >
        {modules.map((module) => (
          <DashboardMetricCard
            key={module.mode}
            icon={module.icon}
            title={module.title}
            value={getCountTotal(counts[module.mode])}
            subtitle="Current operational count"
            chips={getCountBreakdown(counts[module.mode] || []).map((item) => ({ label: item.name || item.type || 'Count', count: item.count || 0 }))}
            onClick={() => navigate(module.path)}
          />
        ))}
      </Box>

      <Box
        sx={{
          mt: 3,
          p: 2,
          borderRadius: 2,
          bgcolor: '#fff',
          border: '1px solid #e6ebef',
          boxShadow: '0 6px 18px rgba(11, 60, 93, 0.08)',
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between">
          <Box>
            <Typography sx={{ color: '#0B3C5D', fontWeight: 800, fontSize: 20 }}>
              Operations Snapshot
            </Typography>
            <Typography sx={{ color: '#667785', mt: 0.5 }}>
              {totalOperations} records are represented across vehicles, bookings, shipments, containers, and jobs.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            <Button startIcon={<AddCircleOutlineIcon />} variant="contained" onClick={() => navigate('/VehicleaddEdit')}>
              Add Vehicle
            </Button>
            <Button variant="outlined" onClick={() => navigate('/addBooking')}>New Booking</Button>
            <Button variant="outlined" endIcon={<KeyboardArrowDownIcon />} onClick={openShipmentMenu}>New Shipment</Button>
            <Menu
              anchorEl={shipmentMenuAnchor}
              open={Boolean(shipmentMenuAnchor)}
              onClose={closeShipmentMenu}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{
                elevation: 10,
                sx: {
                  borderRadius: 3,
                  minWidth: 300,
                  py: 1,
                  bgcolor: theme.palette.background.paper,
                  boxShadow: '0 18px 48px rgba(11, 60, 93, 0.18)',
                },
              }}
            >
              <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
                <Typography sx={{ fontWeight: 700, fontSize: 15, mb: 0.5 }}>
                  Choose Shipment Type
                </Typography>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, lineHeight: 1.4 }}>
                  Select the shipment mode to start adding
                </Typography>
              </Box>
              <MenuItem
                onClick={() => handleAddShipment('FCL')}
                sx={{
                  px: 3,
                  py: 1.5,
                  gap: 2,
                  borderRadius: 3,
                  mb: 1,
                  display: 'flex',
                  alignItems: 'center',
                  bgcolor: '#E8F1FF',
                  '&:hover': { bgcolor: '#D3E6FF' },
                }}
              >
                <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: '#C9DEFF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <LocalShippingIcon sx={{ color: '#1D4DB5', fontSize: 20 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 14 }}>Full Container Load</Typography>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>FCL</Typography>
                </Box>
              </MenuItem>
              <MenuItem
                onClick={() => handleAddShipment('LCL')}
                sx={{
                  px: 3,
                  py: 1.5,
                  gap: 2,
                  borderRadius: 3,
                  mb: 1,
                  display: 'flex',
                  alignItems: 'center',
                  bgcolor: '#FFF4E6',
                  '&:hover': { bgcolor: '#FFE7CC' },
                }}
              >
                <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: '#F6D49F', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <LayersIcon sx={{ color: '#B56B00', fontSize: 20 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 14 }}>Less than Container Load</Typography>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>LCL</Typography>
                </Box>
              </MenuItem>
              <MenuItem
                onClick={() => handleAddShipment('SPL')}
                sx={{
                  px: 3,
                  py: 1.5,
                  gap: 2,
                  borderRadius: 3,
                  display: 'flex',
                  alignItems: 'center',
                  bgcolor: '#EAF9ED',
                  '&:hover': { bgcolor: '#D7F2D4' },
                }}
              >
                <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: '#B8E6B0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CallSplitIcon sx={{ color: '#207A22', fontSize: 20 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 14 }}>Split container loaded</Typography>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>SPL</Typography>
                </Box>
              </MenuItem>
            </Menu>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};

export default AdminDashboardHome;
